// Hero fulfillment row: the average hours from order to shipped over the last 60 days (Josh 10/19; it was Shopify's
// 30-day "Time to fulfill" median from 10/12), from /api/fulfillment (the checkout Worker on the theme; tools/fulfillment_stats.py on serve.py)
// and re-checked every minute while the page is open. Runs the truck while the row is on screen; the * note opens on
// hover, focus or tap.
(() => {
  'use strict';
  const row = document.querySelector('.hero-eta');
  if (!row) return;
  const value = row.querySelector('[data-eta-hours]');
  const note = row.querySelector('.hero-eta-note');
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const POLL_MS = 60000;
  let shown = null;    // hours on screen now (null: nothing shown yet; 0 is a real value)
  let pending = null;  // hours fetched before the row was first seen
  let seen = false;
  let lastFetch = 0;
  let readAt = 0;      // when Shopify was read, on this page's clock (from the answer's ageSeconds)
  const STALE_MS = 3600e3;

  // The Worker sends whole hours (the old median could be a half): show halves if one ever comes.
  const label = n => `${Number.isInteger(n) ? n : n.toFixed(1)} hour${n === 1 ? '' : 's'}`;
  function countTo(hours) {
    const from = shown ?? 0, to = Math.round(hours * 2) / 2;
    shown = to;
    row.dataset.state = 'ready';
    if (reduced || from === to) { value.textContent = label(to); return; }
    const start = performance.now();
    const tick = now => {
      const t = Math.min(1, (now - start) / 900);
      value.textContent = label(t < 1 ? Math.round(from + (to - from) * (1 - Math.pow(1 - t, 3))) : to);
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  // Expiry runs on its own clock, not only when a request ends: a request that hangs must not keep an old number up.
  const expire = () => { if (readAt && Date.now() - readAt > STALE_MS) row.dataset.state = 'off'; };
  function refresh() {
    lastFetch = Date.now();
    const ctl = window.AbortController ? new AbortController() : null;
    if (ctl) setTimeout(() => ctl.abort(), 15000);
    fetch('/api/fulfillment', { cache: 'no-store', signal: ctl?.signal })
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(stats => {
        const hours = stats?.hours ?? stats?.avgHours;
        if (typeof hours !== 'number' || !(hours >= 0)) throw new Error('no number');
        readAt = Date.now() - Math.max(0, Number(stats.ageSeconds) || 0) * 1000;
        if (Date.now() - readAt > STALE_MS) throw new Error('stale');
        if (row.dataset.state === 'off') row.dataset.state = 'loading';   // back on screen, so the first sighting can run
        if (seen) countTo(hours); else pending = hours;
      })
      .catch(() => {
        // Never shown, or Shopify last read over an hour ago: hide the row rather than leave a frozen number up.
        if ((shown === null && pending === null) || !readAt || Date.now() - readAt > STALE_MS) row.dataset.state = 'off';
      });
  }
  refresh();
  setInterval(() => { expire(); if (document.visibilityState === 'visible') refresh(); }, POLL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    expire();
    if (Date.now() - lastFetch > POLL_MS) refresh();
  });

  // Drive only while visible; the first sighting zooms the truck in and counts the hours up.
  const firstSight = () => {
    seen = true;
    row.classList.add('is-arriving', 'is-driving');
    if (pending !== null) countTo(pending);
  };
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      row.classList.toggle('is-paused', !entry.isIntersecting);
      if (entry.isIntersecting && !seen) firstSight();
    }, { threshold: 0.4 }).observe(row);
  } else {
    firstSight();
  }

  // The * note: hover (CSS), keyboard focus, or tap to toggle; Escape or a tap elsewhere closes it.
  const setOpen = open => {
    row.classList.toggle('is-tip-open', open);
    note.setAttribute('aria-expanded', String(open));
  };
  note.addEventListener('click', event => { event.stopPropagation(); setOpen(!row.classList.contains('is-tip-open')); });
  note.addEventListener('focus', () => { if (note.matches(':focus-visible')) setOpen(true); });
  note.addEventListener('blur', () => setOpen(false));
  document.addEventListener('click', event => { if (!row.contains(event.target)) setOpen(false); });
  document.addEventListener('keydown', event => { if (event.key === 'Escape') setOpen(false); });
})();
